import { createContext, useContext, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { get } from "lib/api";
import { cacheCompanyProfiles } from "lib/companyProfile";
import { applyStorefrontDocumentMetadata } from "lib/storefrontMeta";
import {
  clearStorefrontTenant,
  readStorefrontTenant,
  resolveStorefrontLocale,
  setTextLocale,
  writeStorefrontTenant,
} from "lib/storefront";
import {
  getStorefrontPreviewCompanyKey,
  getStorefrontPreviewJwt,
  getStorefrontPreviewTenantOverride,
  isStorefrontPreviewActive,
  subscribeStorefrontPreview,
} from "lib/storefrontPreview";

const StorefrontTenantContext = createContext({
  tenant: null,
  tenantKey: null,
  loading: true,
  error: null,
  reload: async () => {},
  isPreview: false,
});

function mergePreviewTenant(base, override) {
  if (!base && !override) return null;
  const next = { ...(base || {}), ...(override || {}) };
  const key = String(next.companyKey || next.key || "").trim().toLowerCase();
  if (key) {
    next.companyKey = key;
    next.key = key;
  }
  if (override?.storefrontCopy && typeof override.storefrontCopy === "object") {
    next.storefrontCopy = override.storefrontCopy;
  }
  return next;
}

export function StorefrontTenantProvider({ children }) {
  const previewMode = isStorefrontPreviewActive();
  const [tenant, setTenant] = useState(() =>
    previewMode ? null : readStorefrontTenant(),
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previewVersion, setPreviewVersion] = useState(0);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await get("/api/storefront/config");
      const row = response?.data || response?.meta?.company || null;
      if (!row?.companyKey && !row?.key) {
        throw new Error("Storefront is not configured for this domain.");
      }
      const cached = writeStorefrontTenant(row);
      cacheCompanyProfiles([row]);
      const tenantRow = cached || row;
      const tenantKey = String(
        tenantRow?.companyKey || tenantRow?.key || "",
      )
        .trim()
        .toLowerCase();
      setTextLocale(
        resolveStorefrontLocale({ tenant: tenantRow, tenantKey }),
      );
      applyStorefrontDocumentMetadata(tenantRow);
      setTenant(tenantRow);
      window.dispatchEvent(
        new CustomEvent("storefront-tenant-changed", { detail: cached || row }),
      );
    } catch (err) {
      clearStorefrontTenant();
      setTenant(null);
      const message =
        err?.response?.data?.error?.message ||
        err?.message ||
        "Storefront is not configured for this domain.";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const loadPreview = async () => {
    const companyKey = getStorefrontPreviewCompanyKey();
    const override = getStorefrontPreviewTenantOverride();
    if (!companyKey) {
      setTenant(null);
      setLoading(true);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await get("/api/storefront/staff/preview-config", {
        headers: {
          "X-VBS-Company": companyKey,
          "X-Storefront-Preview": "staff",
        },
      });
      const row = response?.data || response?.meta?.company || null;
      if (!row?.companyKey && !row?.key) {
        throw new Error("Storefront preview config is unavailable.");
      }
      const merged = mergePreviewTenant(row, override);
      writeStorefrontTenant(merged);
      cacheCompanyProfiles([merged]);
      const tenantKey = String(merged?.companyKey || merged?.key || "")
        .trim()
        .toLowerCase();
      setTextLocale(resolveStorefrontLocale({ tenant: merged, tenantKey }));
      applyStorefrontDocumentMetadata(merged);
      setTenant(merged);
      window.dispatchEvent(
        new CustomEvent("storefront-tenant-changed", { detail: merged }),
      );
    } catch (err) {
      setTenant(mergePreviewTenant(null, override));
      const message =
        err?.response?.data?.error?.message ||
        err?.message ||
        "Storefront preview config is unavailable.";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (previewMode) {
      loadPreview();
      return subscribeStorefrontPreview(() => {
        setPreviewVersion((value) => value + 1);
      });
    }
    load();
    return undefined;
  }, [previewMode]);

  useEffect(() => {
    if (!previewMode) return;
    if (!getStorefrontPreviewCompanyKey()) return;
    loadPreview();
  }, [previewMode, previewVersion]);

  useEffect(() => {
    if (tenant) applyStorefrontDocumentMetadata(tenant);
  }, [tenant]);

  const value = useMemo(
    () => ({
      tenant,
      tenantKey: tenant?.companyKey || tenant?.key || null,
      loading,
      error,
      reload: previewMode ? loadPreview : load,
      isPreview: previewMode,
      previewJwt: previewMode ? getStorefrontPreviewJwt() : "",
    }),
    [tenant, loading, error, previewMode],
  );

  return (
    <StorefrontTenantContext.Provider value={value}>
      {children}
    </StorefrontTenantContext.Provider>
  );
}

StorefrontTenantProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useStorefrontTenant() {
  return useContext(StorefrontTenantContext);
}
